import { useState } from 'react';

import type { AdminVendor, User } from '@keboola/api-client/developerPortal/types';
import { Alert, Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';
import { Pencil } from '@keboola/design/icons';

import { SaveButton } from '../../../components/SaveButton';
import { selectSaveButtonState } from '../../../components/saveButtonState';
import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useSetUserVendor } from '../queries';
import { manageUserVendorsFormSchema } from '../schemas';
import { vendorNameById } from '../users';

const FORM_ID = 'manage-user-vendors';

type Props = { user: User; vendors: readonly AdminVendor[] };

/**
 * Adds a user to a vendor, or removes them from one.
 *
 * - The vendor list depends on the action, so no no-op can be filed.
 * - Removal maps `user.vendors` directly, so an orphaned membership (no vendor record) stays removable.
 */
export function ManageUserVendorsModal({ user, vendors }: Props) {
  const [open, setOpen] = useState(false);
  const setUserVendor = useSetUserVendor();

  const optionsFor = (action: 'add' | 'remove') =>
    action === 'add'
      ? vendors
          .filter((vendor) => !(user.vendors ?? []).includes(vendor.id))
          .map((vendor) => ({
            value: vendor.id,
            label: vendor.isPublic ? vendor.name : `${vendor.name} (private)`,
          }))
      : (user.vendors ?? []).map((id) => ({ value: id, label: vendorNameById(id, vendors) }));

  const form = useAppForm({
    defaultValues: { action: 'add' as 'add' | 'remove', vendor: '' },
    validators: formValidators(manageUserVendorsFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await setUserVendor.mutateAsync({
          action: value.action,
          username: user.username,
          vendorId: value.vendor,
        });
        toast.success(
          value.action === 'add'
            ? `${user.username} was added to ${vendorNameById(value.vendor, vendors)}.`
            : `${user.username} was removed from ${vendorNameById(value.vendor, vendors)}.`,
        );
        setOpen(false);
        form.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not update the membership.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset();
      }}
    >
      <Modal.Trigger asChild>
        <Button
          variant="invisible"
          size="icon-sm"
          aria-label={`Manage vendors for ${user.username}`}
        >
          <Pencil />
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header description={user.username}>Update membership in vendors</Modal.Header>
        <Modal.Body>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField
                name="action"
                listeners={{
                  // The vendor list changes with the action; `resetField` also clears error/blur state.
                  onChange: () => form.resetField('vendor'),
                }}
              >
                {(field) => (
                  <field.SelectField
                    label="Action"
                    items={[
                      { value: 'add', label: 'Add to a vendor' },
                      { value: 'remove', label: 'Remove from a vendor' },
                    ]}
                  />
                )}
              </form.AppField>

              <form.Subscribe selector={(state) => state.values.action}>
                {(action) => {
                  const options = optionsFor(action);

                  return options.length === 0 ? (
                    <Alert variant="info">
                      {action === 'add'
                        ? 'This user already belongs to every vendor.'
                        : 'This user does not belong to any vendor yet.'}
                    </Alert>
                  ) : (
                    <form.AppField name="vendor">
                      {(field) => (
                        <field.SelectField
                          label="Vendor"
                          required
                          items={options}
                          placeholder="Select vendor"
                        />
                      )}
                    </form.AppField>
                  );
                }}
              </form.Subscribe>

              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <form.Subscribe
            selector={(state) => ({ action: state.values.action, ...selectSaveButtonState(state) })}
          >
            {({ action, ...save }) =>
              // No options: `vendor` stays `''` and its error has no control to show on.
              optionsFor(action).length === 0 ? null : (
                // `canSubmitWhenInvalid: true` keeps `canSubmit` true, so the pristine gate is needed.
                <SaveButton state={save} form={FORM_ID}>
                  Update membership
                </SaveButton>
              )
            }
          </form.Subscribe>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
